import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { useTheme } from 'styled-components'
import { connect } from 'react-redux'
import Div100vh from 'react-div-100vh'
import { useTranslation } from 'react-i18next'
import jwt_decode from 'jwt-decode'
import { useStatsigClient } from '@statsig/react-bindings'
import ModalActions from 'reducers/Modal'
import ReleaseActions from 'reducers/Releases'
import ReleaseModal from 'components/Modals/ReleaseModal'
import QuickViewModal from 'components/Modals/QuickViewModal'
import GlobalOverlaysWithRollout from 'components/GlobalOverlaysWithRollout'
import AppShellWithRollout from 'components/AppShellWithRollout'
import ResponsiveBreakpointsLegacyScope from 'components/ResponsiveBreakpointsLegacyScope'
import { Playbar, Sidebar, DialogModal } from 'components'
import { RELEASE_QUICKVIEW_MODAL } from 'constants/featureFlags'
import useLocalStorage from 'utils/hooks/useLocalStorage'
import useNewPlaybar from 'utils/hooks/useNewPlaybar'
import UserVerificationBanner from 'components/Banner/UserVerificationBanner'
import { useDesktopMediaQuery } from 'utils/hooks/useMediaQueries'

const mapStateToProps = ({ releases, setting, modal, global }) => ({
  selectedReleaseInModal: releases.selectedReleaseInModal,
  isSidebarOpen: setting.isSidebarOpen || false,
  showReleaseErrorModal: modal.showReleaseErrorModal,
  releaseErrorTitle: modal.releaseErrorTitle,
  releaseErrorMessage: modal.releaseErrorMessage,
  isIpadView: global.isIpadView || false
})

const ResponsiveLayoutContent = ({
  menu,
  content,
  isIpadView,
  isSidebarOpen,
  showReleaseErrorModal,
  releaseErrorTitle,
  releaseErrorMessage,
  selectedReleaseInModal,
  dispatch,
  contentContainerStyles
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { checkGate, isLoading: isStatsigLoading } = useStatsigClient()
  const [verificationBannerData, setVerificationBannerData] = useLocalStorage(
    'verificationBannerData',
    {}
  )
  const isDesktop = useDesktopMediaQuery()
  const { shouldShow, lastClosed } = verificationBannerData
  const isQuickViewModalEnabled =
    !isStatsigLoading && checkGate(RELEASE_QUICKVIEW_MODAL)
  const isNewPlaybarEnabled = useNewPlaybar()

  useEffect(() => {
    const now = new Date().getTime()
    const seventyTwoHoursInMilliseconds = 72 * 60 * 60 * 1000

    if (isEmpty(verificationBannerData)) {
      setVerificationBannerData({ shouldShow: true, lastClosed: now })
      return
    }

    if (now - lastClosed >= seventyTwoHoursInMilliseconds) {
      setVerificationBannerData({ shouldShow: true, lastClosed: now })
    }
  }, [])

  const handleYesButton = useCallback(() => {
    dispatch(ModalActions.dismissReleaseError())
  }, [])

  const handleCloseReleaseModal = useCallback(() => {
    dispatch(ReleaseActions.toggleReleaseInModal(null))
  }, [])

  const handleBannerClose = () => {
    setVerificationBannerData({
      shouldShow: false,
      lastClosed: new Date().getTime()
    })
  }

  const renderReleaseModal = () => {
    if (!selectedReleaseInModal) {
      return null
    }

    const ReleaseModalComponent = isQuickViewModalEnabled
      ? QuickViewModal
      : ReleaseModal

    const releaseModal = (
      <div className="absolute flex w-full h-full justify-around items-center">
        <ReleaseModalComponent
          release={selectedReleaseInModal}
          isIpadView={isIpadView}
          onClickCloseButton={handleCloseReleaseModal} />
      </div>
    )

    return isQuickViewModalEnabled ? (
      <GlobalOverlaysWithRollout>
        {releaseModal}
      </GlobalOverlaysWithRollout>
    ) : (
      releaseModal
    )
  }

  const renderReleaseErrorModal = () => {
    if (showReleaseErrorModal) {
      return (
        <DialogModal
          hideCancel
          title={t(releaseErrorTitle)}
          okButtonTitle={t('general.ok')}
          onClickYes={handleYesButton}>
          <div className="w-full flex flex-col items-center mb-6">
            <div className="mt-12 mb-6">{t(releaseErrorMessage)}</div>
          </div>
        </DialogModal>
      )
    } else {
      return null
    }
  }

  const token = localStorage.getItem('accessToken')
  const currentUser = jwt_decode(token)
  const label = currentUser.label_staff
  const verifiedUser = currentUser.verified
  const showSideNav = isSidebarOpen || isDesktop

  return (
    <Div100vh>
      {/* need an id on mainLayout so that can use it to calculate items per row */}
      <div
        id="mainLayout"
        className="h-full w-full flex"
        style={{
          backgroundColor: theme.colors?.neutralW10,
          display: 'flex',
          overflow: 'hidden'
        }}>
        {showSideNav && <Sidebar />}
        <div
          className="flex flex-col flex-1 min-w-0 relative"
          style={{ overflow: 'hidden' }}>
          {menu}

          <div
            style={{
              overflow: 'auto',
              flex: 1,
              minHeight: 0,
              ...contentContainerStyles
            }}
            className="content-scrollable-area">
            <ResponsiveBreakpointsLegacyScope>
              {content}
            </ResponsiveBreakpointsLegacyScope>
          </div>

          {/* The redesigned playbar floats over the scroll area and lets
              clicks through to the content behind it; the legacy one is a
              sticky block in normal flow. */}
          <div
            className={
              isNewPlaybarEnabled
                ? 'absolute bottom-0 left-0 right-0 w-full z-30'
                : 'flex-none -bottom-1 w-full'
            }
            style={
              isNewPlaybarEnabled
                ? { pointerEvents: 'none' }
                : { position: 'sticky', bottom: 0, zIndex: 1 }
            }>
            {!label && !verifiedUser && shouldShow && (
              <UserVerificationBanner onClose={handleBannerClose} />
            )}
            <ResponsiveBreakpointsLegacyScope>
              <Playbar />
            </ResponsiveBreakpointsLegacyScope>
          </div>
        </div>
        <ResponsiveBreakpointsLegacyScope>
          {renderReleaseModal()}
          {renderReleaseErrorModal()}
        </ResponsiveBreakpointsLegacyScope>
      </div>
    </Div100vh>
  )
}

const responsiveLayoutPropTypes = {
  menu: PropTypes.element,
  content: PropTypes.element,
  isIpadView: PropTypes.bool,
  isSidebarOpen: PropTypes.bool,
  showReleaseErrorModal: PropTypes.bool,
  releaseErrorTitle: PropTypes.string,
  releaseErrorMessage: PropTypes.string,
  selectedReleaseInModal: PropTypes.object,
  dispatch: PropTypes.func,
  contentContainerStyles: PropTypes.object
}

ResponsiveLayoutContent.propTypes = responsiveLayoutPropTypes

const ResponsiveLayout = (props) => (
  <AppShellWithRollout>
    <ResponsiveLayoutContent
      menu={props.menu}
      content={props.content}
      isIpadView={props.isIpadView}
      isSidebarOpen={props.isSidebarOpen}
      showReleaseErrorModal={props.showReleaseErrorModal}
      releaseErrorTitle={props.releaseErrorTitle}
      releaseErrorMessage={props.releaseErrorMessage}
      selectedReleaseInModal={props.selectedReleaseInModal}
      dispatch={props.dispatch}
      contentContainerStyles={props.contentContainerStyles}
    />
  </AppShellWithRollout>
)

ResponsiveLayout.propTypes = responsiveLayoutPropTypes

export default connect(mapStateToProps)(ResponsiveLayout)
